import { ILaya } from "../../ILaya";
import { Matrix } from "../maths/Matrix";
import { Point } from "../maths/Point";
import { Stat } from "../utils/Stat";
import { SpriteConst, TransformKind } from "./SpriteConst";
export class SpriteGlobalTransform {
    constructor(sp) {
        this._flags = 0;
        this._x = 0.0;
        this._y = 0.0;
        this._rot = 0.0;
        this._scaleX = 1.0;
        this._scaleY = 1.0;
        this._modifiedFrame = 0;
        this._sp = sp;
        this.cache = true;
        this._notifyRenderSpriteTransChange();
    }
    get cache() {
        return this._cache;
    }
    set cache(value) {
        if (value) {
            this._setFlag(TransformKind.Matrix | TransformKind.TRS, true);
        }
        this._cache = value;
    }
    getMatrix() {
        if (this._matrix == null)
            this._matrix = new Matrix();
        if (this._cache && !this._getFlag(TransformKind.Matrix))
            return this._matrix;
        let sp = this._sp;
        this._matrix.setMatrix(sp._x, sp._y, sp._scaleX, sp._scaleY, sp._rotation, sp._skewX, sp._skewY, sp._pivotX, sp._pivotY);
        if (sp._parent) {
            Matrix.mul(this._matrix, sp._parent.globalTrans.getMatrix(), this._matrix);
            this._setFlag(TransformKind.Matrix, false);
        }
        else if (sp._maskParent) {
            Matrix.mul(this._matrix, sp._maskParent.globalTrans.getMatrix(), this._matrix);
            this._setFlag(TransformKind.Matrix, false);
        }
        return this._matrix;
    }
    getMatrixInv(out) {
        this.getMatrix().copyTo(out);
        out.invert();
        return out;
    }
    get x() {
        return this.getPos(tmpPoint).x;
    }
    get y() {
        return this.getPos(tmpPoint).y;
    }
    getSceneMatrix(out) {
        if (!this._sp.scene)
            return this.getMatrix();
        this._sp.scene.globalTrans.getMatrixInv(tmpMarix).copyTo(out);
        Matrix.mul(this.getMatrix(), out, out);
        return out;
    }
    getScenePos(out) {
        if (!this._sp.scene)
            return this.getPos(out);
        return this._sp.scene.globalTrans.getMatrixInv(tmpMarix).transformPoint(this.getPos(out));
    }
    getSceneScale(out) {
        out.x = this.scaleX;
        out.y = this.scaleY;
        if (this._sp.scene) {
            const mat = this._sp.scene.globalTrans.getMatrix();
            out.x /= mat.getScaleX();
            out.y /= mat.getScaleY();
        }
        return out;
    }
    getSceneRotation() {
        let angle = this.rotation;
        if (this._sp.scene)
            angle -= this._sp.scene.globalTrans.rotation;
        return angle;
    }
    getPos(out) {
        if (this._cache) {
            this._cachePos();
            out.x = this._x;
            out.y = this._y;
        }
        else {
            this._sp.localToGlobal(out.setTo(0, 0), false, null);
        }
        return out;
    }
    setPos(x, y) {
        let sp = this._sp;
        if (this._cache) {
            this._cachePos();
            if (x == this._x && y == this._y)
                return;
            let point = sp._parent.globalTrans.getMatrix().invertTransformPoint(tmpPoint.setTo(x, y));
            this._cache = false;
            sp.pos(point.x, point.y);
            this._cache = true;
            this._x = x;
            this._y = y;
            this._setFlag(TransformKind.Pos, false);
            this._setFlag(TransformKind.Matrix, true);
            this._syncFlag(TransformKind.Pos | TransformKind.Matrix, true);
        }
        else {
            tmpPoint.setTo(x, y);
            let point = sp.globalToLocal(tmpPoint, false, null);
            point = sp.toParentPoint(point);
            sp.pos(point.x, point.y);
        }
    }
    get rotation() {
        let sp = this._sp;
        if (this._cache) {
            if (this._getFlag(TransformKind.Rotation)) {
                this._setFlag(TransformKind.Rotation, false);
                if (sp._parent == sp._scene || !sp._parent)
                    this._rot = sp._rotation;
                else
                    this._rot = sp._rotation + sp._parent.globalTrans.rotation;
            }
            return this._rot;
        }
        else {
            let angle = 0;
            let ele = sp;
            while (ele) {
                if (ele === sp._scene)
                    break;
                angle += ele._rotation;
                ele = ele._parent;
            }
            return angle;
        }
    }
    set rotation(value) {
        if (value == this.rotation) {
            return;
        }
        let sp = this._sp;
        if (sp._parent == sp._scene || !sp._parent) {
            sp.rotation = value;
        }
        else {
            sp.rotation = value - sp._parent.globalTrans.rotation;
        }
        if (this._cache) {
            this._rot = value;
            this._setFlag(TransformKind.Rotation, false);
            this._setFlag(TransformKind.Matrix, true);
            this._syncFlag(TransformKind.Matrix, true);
        }
    }
    get scaleX() {
        if (this._cache) {
            this._cacheScale();
            return this._scaleX;
        }
        else {
            let scale = 1;
            let ele = this._sp;
            while (ele) {
                if (ele === ILaya.stage)
                    break;
                scale *= ele._scaleX;
                ele = ele._parent;
            }
            return scale;
        }
    }
    get scaleY() {
        if (this._cache) {
            this._cacheScale();
            return this._scaleY;
        }
        else {
            let scale = 1;
            let ele = this._sp;
            while (ele) {
                if (ele === ILaya.stage)
                    break;
                scale *= ele._scaleY;
                ele = ele._parent;
            }
            return scale;
        }
    }
    _cachePos() {
        if (this._getFlag(TransformKind.Matrix | TransformKind.Pos)) {
            this._setFlag(TransformKind.Pos, false);
            let p = this.getMatrix().transformPoint(tmpPoint.setTo(this._sp.pivotX, this._sp.pivotY));
            this._x = p.x;
            this._y = p.y;
        }
    }
    _cacheScale() {
        if (this._getFlag(TransformKind.Matrix | TransformKind.Scale)) {
            this._setFlag(TransformKind.Scale, false);
            let mat = this.getMatrix();
            this._scaleX = mat.getScaleX();
            this._scaleY = mat.getScaleY();
        }
    }
    _getFlag(type) {
        return (this._flags & type) != 0;
    }
    _setFlag(type, value, notify = true) {
        if (value)
            this._flags |= type;
        else
            this._flags &= ~type;
        if (value) {
            this._sp._struct.dcBoundsTarget = null;
            this._sp.event(SpriteGlobalTransform.CHANGED, type);
            if (notify) {
                this._notifyRenderSpriteTransChange();
            }
        }
    }
    _notifyRenderSpriteTransChange() {
        if ((this._sp._renderType & SpriteConst.UPDATETRANS)) {
            ILaya.stage._tranMatrixUpdateList.add(this._sp);
            this._modifiedFrame = Stat.loopCount;
        }
    }
    _syncFlag(flag, value) {
        if (this._cache) {
            for (let child of this._sp._children) {
                let globaltrans = child.globalTrans;
                if (globaltrans) {
                    globaltrans._setFlag(flag, value);
                    globaltrans._syncFlag(flag, value);
                }
            }
        }
    }
    _spTransChanged(kind) {
        if (this._cache)
            this._setFlag(kind | TransformKind.Matrix, true);
        this._syncFlag(kind | TransformKind.Matrix, true);
    }
    localToGlobal(x, y) {
        if (this._cache) {
            return this.getMatrix().transformPoint(tmpPoint.setTo(this._sp.pivotX + x, this._sp.pivotY + y));
        }
        else {
            return this._sp.localToGlobal(tmpPoint.setTo(x, y));
        }
    }
    globalToLocal(x, y) {
        if (this._cache) {
            let point = this.getMatrix().invertTransformPoint(tmpPoint.setTo(x, y));
            point.x -= this._sp.pivotX;
            point.y -= this._sp.pivotY;
            return point;
        }
        else {
            return this._sp.globalToLocal(tmpPoint.setTo(x, y));
        }
    }
}
SpriteGlobalTransform.CHANGED = "globalTransChanged";
const tmpPoint = new Point();
const tmpMarix = new Matrix();
